/**
 * Bundle Builder pieces — the mockup-step-5 controls that live inside the
 * pricing dialog's "Build new bundle" mode (issue #235, spec 48).
 *
 * Three exports, all controlled by the dialog (no hidden state beyond the
 * template fetch):
 *  - `BundleCompositionBuilder` — name, per-size steppers, template chips
 *    (+ "Save as template"), live preview, total pieces, missing-size prompt.
 *  - `ColorSpecPicker` — Mixed | Single Color toggle + swatches from the
 *    product's Color axis. Single behaves as a radio (exactly one).
 *  - `swatchColor` — CSS colour for a value name, neutral fallback.
 *
 * The completeness rule mirrored here is `assertValidComposition`
 * (`pack-rules.ts`): every size needs a quantity ≥ 1, pieces ≤ 1000. The
 * server remains the floor; this is the inline mirror, using the same refusal
 * copy as the pack dialog.
 */
import { useState } from "react";
import { Minus, Plus, Save } from "lucide-react";
import { toast } from "@flint/ui";
import {
  DEFAULT_BUNDLE_TEMPLATES,
  applyTemplate,
} from "@flint/domain/commerce/store/bundle-template-rules";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";

export type ColorMode = "mixed" | "single";

/** CSS colour for a swatch, or null when the name isn't a real CSS colour. */
export function swatchColor(name: string): string | null {
  const candidate = name.trim().toLowerCase().replace(/\s+/g, "");
  if (typeof CSS !== "undefined" && CSS.supports?.("color", candidate)) return candidate;
  return null;
}

// ── colour picker ─────────────────────────────────────────────────

export function ColorSpecPicker({
  colors,
  mode,
  selected,
  onModeChange,
  onSelectedChange,
}: {
  /** The product's Color axis values — the only colours offerable. */
  colors: readonly string[];
  mode: ColorMode;
  selected: readonly string[];
  onModeChange: (mode: ColorMode) => void;
  onSelectedChange: (colors: string[]) => void;
}) {
  const toggle = (color: string) => {
    if (mode === "single") {
      // Radio behaviour: Single means exactly one (decision #235-Q4).
      onSelectedChange([color]);
      return;
    }
    onSelectedChange(
      selected.includes(color) ? selected.filter((c) => c !== color) : [...selected, color],
    );
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        role="radiogroup"
        aria-label="Colour mode"
        className="flex h-9 w-fit items-center gap-1 rounded-sm border border-line bg-raised p-1"
      >
        {(["mixed", "single"] as const).map((m) => (
          <button
            key={m}
            type="button"
            role="radio"
            aria-checked={mode === m}
            onClick={() => {
              onModeChange(m);
              // Narrow, don't remember: switching to Single keeps only the
              // first selection so the state can't silently violate the mode.
              if (m === "single" && selected.length > 1) {
                onSelectedChange(selected.slice(0, 1));
              }
            }}
            className={`inline-flex h-full items-center rounded-[3px] px-3 text-sm transition-colors ${
              mode === m
                ? "bg-paper font-medium text-ink shadow"
                : "text-muted hover:text-ink cursor-pointer"
            }`}
          >
            {m === "mixed" ? "Mixed" : "Single Color"}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {colors.map((color) => {
          const css = swatchColor(color);
          const active = selected.includes(color);
          return (
            <button
              key={color}
              type="button"
              aria-pressed={active}
              title={color}
              onClick={() => toggle(color)}
              className={`inline-flex size-8 items-center justify-center rounded-full border-2 text-[10px] font-semibold uppercase transition-shadow ${
                active ? "border-sky-600 ring-2 ring-sky-100" : "border-line hover:border-line-2"
              } ${css ? "" : "bg-raised text-ink-2"}`}
              {...(css ? { style: { backgroundColor: css } } : {})}
            >
              {css ? "" : color.slice(0, 2)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── composition builder ───────────────────────────────────────────

/**
 * One stepper row of the builder. In variant mode a row is a generated
 * variant ("S · Black", sublabel = its SKU); in size mode it is a bare size
 * label — the fallback when a product has a Size axis but no variants yet.
 */
export interface CompositionRow {
  key: string;
  label: string;
  sublabel?: string | undefined;
}

export function BundleCompositionBuilder({
  rows,
  requireAll,
  name,
  onNameChange,
  qty,
  onQtyChange,
  colorLine,
}: {
  rows: readonly CompositionRow[];
  /**
   * Size mode: every row needs a qty ≥ 1 (spec 13 R2 — the scale's labels
   * must all be covered). Variant mode: false — picking WHICH variants go in
   * is the point, and the scale is minted from the sizes actually used.
   */
  requireAll: boolean;
  name: string;
  onNameChange: (name: string) => void;
  qty: Record<string, number>;
  onQtyChange: (next: Record<string, number>) => void;
  /** "Mixed colors" / "Black" — echoed in the live preview. */
  colorLine: string | null;
}) {
  const templates = trpc.storeBundleTemplates.list.useQuery();
  const createTemplate = trpc.storeBundleTemplates.create.useMutation();
  const utils = trpc.useUtils();
  const [savingTemplate, setSavingTemplate] = useState(false);

  const keys = rows.map((r) => r.key);
  const pieces = keys.reduce((sum, k) => sum + (qty[k] ?? 0), 0);
  const missing = requireAll
    ? rows.filter((r) => !((qty[r.key] ?? 0) > 0)).map((r) => r.label)
    : [];
  const maxQty = Math.max(1, ...keys.map((k) => qty[k] ?? 0));

  const step = (key: string, delta: number) => {
    const next = Math.min(1000, Math.max(0, (qty[key] ?? 0) + delta));
    onQtyChange({ ...qty, [key]: next });
  };

  // Tenant templates when any exist; the five mockup presets otherwise.
  // Applying a preset never persists it — only "Save as template" writes.
  const chips =
    (templates.data?.length ?? 0) > 0
      ? (templates.data ?? []).map((t) => ({
          name: t.name,
          quantities: t.quantities,
          colorMode: t.colorMode ?? undefined,
        }))
      : DEFAULT_BUNDLE_TEMPLATES.map((t) => ({ ...t }));

  const saveAsTemplate = async () => {
    const pattern = keys.map((k) => qty[k] ?? 0).filter((n) => n > 0);
    if (pattern.length === 0) return;
    setSavingTemplate(true);
    try {
      await createTemplate.mutateAsync({
        name: name.trim() || "Template",
        quantities: keys.map((k) => Math.max(1, qty[k] ?? 1)),
      });
      await utils.storeBundleTemplates.list.invalidate();
      toast.success("Template saved", {
        description: "Available on every product's bundle builder.",
      });
    } catch (err) {
      toast.error("Couldn't save template", { description: storeErrorMessage(err) });
    } finally {
      setSavingTemplate(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_300px]">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="bundle-name"
            className="text-sm font-medium leading-[18px] text-ink-2"
          >
            Bundle name *
          </label>
          <input
            id="bundle-name"
            value={name}
            maxLength={32}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Bundle A"
            className="h-9 rounded-sm border border-line bg-paper px-3 text-sm text-ink transition-colors placeholder:text-placeholder hover:border-line-2 focus-visible:border-sky-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-sky-100"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-sm font-medium leading-[18px] text-ink-2">
            Bundle composition
          </span>
          {/* Two-up on wide dialogs: a full matrix (sizes × colours) as a
              single column is a long scroll; side-by-side halves it. */}
          <div className="grid max-h-72 grid-cols-1 gap-1.5 overflow-y-auto pr-1 sm:grid-cols-2">
            {rows.map((row) => (
              <div
                key={row.key}
                className="flex items-center justify-between gap-3 rounded-sm border border-line px-3 py-1.5"
              >
                <span className="flex min-w-0 flex-col text-sm text-ink">
                  <span className="truncate" title={row.label}>
                    {row.label}
                  </span>
                  {row.sublabel ? (
                    <span
                      className="truncate font-mono text-[11px] leading-4 text-muted"
                      title={row.sublabel}
                    >
                      {row.sublabel}
                    </span>
                  ) : null}
                </span>
                <div className="flex shrink-0 items-center gap-1">
                  <button
                    type="button"
                    aria-label={`Decrease ${row.label}`}
                    className="inline-flex size-7 items-center justify-center rounded-sm border border-line text-muted transition-colors hover:bg-raised hover:text-ink"
                    onClick={() => step(row.key, -1)}
                  >
                    <Minus className="size-3.5" aria-hidden />
                  </button>
                  <input
                    value={qty[row.key] ?? 0}
                    inputMode="numeric"
                    aria-label={`Quantity — ${row.label}`}
                    onChange={(e) =>
                      onQtyChange({
                        ...qty,
                        [row.key]: Math.min(1000, Math.max(0, Number(e.target.value) || 0)),
                      })
                    }
                    className="h-7 w-12 rounded-sm border border-line bg-paper text-center text-sm tabular-nums text-ink [appearance:textfield]"
                  />
                  <button
                    type="button"
                    aria-label={`Increase ${row.label}`}
                    className="inline-flex size-7 items-center justify-center rounded-sm border border-line text-muted transition-colors hover:bg-raised hover:text-ink"
                    onClick={() => step(row.key, 1)}
                  >
                    <Plus className="size-3.5" aria-hidden />
                  </button>
                </div>
              </div>
            ))}
          </div>
          {/* Reserved line. Size mode carries the pack-dialog's exact
              completeness copy; variant mode only needs *something* picked. */}
          <p className={`min-h-5 text-xs ${missing.length > 0 ? "text-bad" : "text-muted"}`}>
            {missing.length > 0
              ? `Every size needs a quantity — ${missing.join(", ")} ${
                  missing.length === 1 ? "is" : "are"
                } empty.`
              : pieces > 0
                ? `${pieces} pieces per bundle`
                : requireAll
                  ? ""
                  : "Set a quantity on each variant this bundle includes."}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted">Templates:</span>
          {chips.map((t) => (
            <button
              key={t.name}
              type="button"
              onClick={() => onQtyChange(applyTemplate(t.quantities, keys))}
              className="inline-flex h-7 items-center rounded-full border border-line bg-paper px-2.5 text-xs text-ink-2 transition-colors hover:border-line-2 hover:bg-raised"
            >
              {t.name}
            </button>
          ))}
          <button
            type="button"
            disabled={savingTemplate || pieces === 0}
            onClick={() => void saveAsTemplate()}
            className="inline-flex h-7 items-center gap-1 rounded-full border border-dashed border-line px-2.5 text-xs text-muted transition-colors hover:border-line-2 hover:text-ink disabled:opacity-40"
          >
            <Save className="size-3" aria-hidden />
            Save as template
          </button>
        </div>
      </div>

      {/* Live preview — the mockup's right panel: name, colour line, per-size
          bars scaled to the largest quantity, total. */}
      <div className="flex flex-col gap-2 rounded-sm border border-line bg-raised px-3.5 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-accent">Live preview</p>
        <p className="text-sm font-semibold text-ink">{name.trim() || "Bundle"}</p>
        {colorLine ? <p className="text-xs text-muted">{colorLine}</p> : null}
        <div className="mt-1 flex flex-1 flex-wrap items-end justify-start gap-x-3 gap-y-2">
          {rows
            .filter((r) => (qty[r.key] ?? 0) > 0 || requireAll)
            .map((row) => {
              const q = qty[row.key] ?? 0;
              return (
                <div key={row.key} className="flex flex-col items-center gap-1">
                  <span className="text-[10px] font-medium tabular-nums text-accent">×{q}</span>
                  <div
                    className="w-6 rounded-sm bg-accent"
                    style={{ height: `${8 + Math.round((q / maxQty) * 48)}px` }}
                    aria-hidden
                  />
                  <span className="max-w-14 truncate text-center text-[10px] text-muted" title={row.label}>
                    {row.label}
                  </span>
                </div>
              );
            })}
        </div>
        <div className="mt-2 flex items-center justify-between rounded-sm border border-line bg-paper px-2.5 py-1.5">
          <span className="text-xs text-muted">Total</span>
          <span className="text-sm font-semibold tabular-nums text-ink">{pieces} Pieces</span>
        </div>
      </div>
    </div>
  );
}
